import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import { GraduationCap, Eye, EyeOff } from 'lucide-react';

const Login = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [institutionName, setInstitutionName] = useState('Institution');
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [userRole, setUserRole] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('role') || 'student';
  });

  useEffect(() => {
    loadInstitutionData();
    const urlParams = new URLSearchParams(location.search);
    const role = urlParams.get('role') || 'student';
    setUserRole(role);
  }, [location.search]);

  const loadInstitutionData = () => {
    try {
      const selectedInstitutionString = localStorage.getItem('selectedInstitution');
      if (selectedInstitutionString) {
        const selectedInstitution = JSON.parse(selectedInstitutionString);
        setInstitutionName(selectedInstitution.name || 'Institution');
      }
    } catch (error) {
      console.error('Error loading institution data:', error);
    }
  };

  const from = location.state?.from?.pathname || '/dashboard';

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        if (result.user.role === 'lecturer') {
          navigate('/lecturer-dashboard', { replace: true });
        } else {
          navigate(from, { replace: true });
        }
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Get the institutionId from localStorage for Google sign-in
  const getInstitutionId = () => {
    try {
      const s = localStorage.getItem('selectedInstitution');
      return s ? JSON.parse(s).id : null;
    } catch { return null; }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setGoogleLoading(true);
    setError('');
    try {
      const institutionId = getInstitutionId();
      const result = await loginWithGoogle(credentialResponse.credential, institutionId);
      if (result.success) {
        navigate(from, { replace: true });
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('Google sign-in failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google sign-in was cancelled or failed. Please try again.');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 via-white to-primary-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl flex items-center justify-center mb-6 shadow-lg transform -rotate-3">
            <GraduationCap className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
            {userRole === 'lecturer' ? 'Lecturer Portal' : 'Welcome Back'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            Sign in to your {institutionName} account
          </p>

          {userRole === 'lecturer' ? (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-xl flex flex-col items-center gap-2">
              <span className="text-xs font-semibold text-blue-800 uppercase tracking-wider">Interested in teaching?</span>
              <div className="flex flex-col gap-2">
                <Link to="/lecturer/apply" className="text-sm font-bold text-blue-600 hover:text-blue-700 flex items-center justify-center gap-1 group">
                  Apply to join our faculty
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </Link>
                <Link to="/lecturer/onboarding/status" className="text-xs font-medium text-gray-500 hover:text-blue-600 transition-colors">
                  Already applied? Check your status
                </Link>
              </div>
            </div>
          ) : (
            <div className="mt-6 inline-flex items-center px-4 py-2 bg-emerald-50 border border-emerald-100 rounded-full">
              <span className="text-xs font-medium text-emerald-800">Are you a lecturer?</span>
              <Link to="/lecturer/apply" className="ml-2 text-xs font-bold text-emerald-600 hover:text-emerald-700 underline">
                Apply to teach here →
              </Link>
            </div>
          )}
        </div>

        {/* Login Form */}
        <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-200">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="form-label">Email Address</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="form-input"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="form-label">Password</label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  className="form-input pr-10"
                  placeholder="Enter your password"
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-error-50 border border-error-200 rounded-lg p-3">
                <p className="text-sm text-error-600">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || googleLoading}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Google Sign-In — students only */}
          {userRole !== 'lecturer' && (
            <div className="mt-6">
              {/* Divider */}
              <div className="relative flex items-center">
                <div className="flex-grow border-t border-gray-200"></div>
                <span className="flex-shrink mx-3 text-xs text-gray-400 font-medium uppercase tracking-wider">or continue with</span>
                <div className="flex-grow border-t border-gray-200"></div>
              </div>

              <div className="mt-4 flex justify-center">
                {googleLoading ? (
                  <div className="flex items-center justify-center w-full py-2 text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600 mr-2"></div>
                    Signing in with Google...
                  </div>
                ) : (
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                    useOneTap={false}
                    shape="rectangular"
                    size="large"
                    text="signin_with"
                    width="368"
                  />
                )}
              </div>
            </div>
          )}

          {/* Additional Links */}
          <div className="mt-6 text-center space-y-2">
            <div className="text-sm">
              <Link to="/forgot-password" className="text-primary-600 hover:text-primary-500 font-medium">
                Forgot your password?
              </Link>
            </div>
            <div className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="text-primary-600 hover:text-primary-500 font-medium">
                Sign up here
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-xs text-gray-500">
            © 2024 {institutionName} Learning Management System. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
